import { useState, useCallback } from 'react';

interface UsePaginationProps {
  pageSize?: number;
}

interface UsePaginationReturn<T> {
  pageStart: number;
  pageEnd: number;
  pageLeft: () => void;
  pageRight: (totalItems: number) => void;
  resetPagination: () => void;
  getPaginatedItems: (items: T[]) => T[];
}

export function usePagination<T>({ pageSize = 50 }: UsePaginationProps = {}): UsePaginationReturn<T> {
  const [pageStart, setPageStart] = useState<number>(0);
  const [pageEnd, setPageEnd] = useState<number>(pageSize);

  const pageLeft = useCallback(() => {
    if (pageStart > 0) {
      const newStart = Math.max(0, pageStart - pageSize);
      setPageStart(newStart);
      setPageEnd(newStart + pageSize);
    }
  }, [pageStart, pageSize]);

  const pageRight = useCallback((totalItems: number) => {
    if (pageEnd < totalItems) {
      const newStart = pageStart + pageSize;
      const newEnd = Math.min(totalItems, newStart + pageSize);
      setPageStart(newStart);
      setPageEnd(newEnd);
    }
  }, [pageStart, pageEnd, pageSize]);

  const resetPagination = useCallback(() => {
    setPageStart(0);
    setPageEnd(pageSize);
  }, [pageSize]);

  const getPaginatedItems = useCallback((items: T[]): T[] => {
    return items.slice(pageStart, pageEnd);
  }, [pageStart, pageEnd]);

  return {
    pageStart,
    pageEnd,
    pageLeft,
    pageRight,
    resetPagination,
    getPaginatedItems,
  };
}
